import { gql, type QueryHookOptions } from "@apollo/client";
import type { Invoice } from "src/__generated__/base.gql";
import {
  type InvoiceForReceiptQuery,
  type InvoiceForReceiptQueryVariables,
  useInvoiceForReceiptQuery,
} from "./__generated__/useFetchPrintableInvoice.gql";

type PrintableInvoiceResult = {
  invoice: Partial<Invoice>;
};

gql`
  query invoiceForReceipt($invoiceId: String!) {
    invoice(invoiceId: $invoiceId) {
      _id
      invoiceNumber
      patientType
      patient {
        cardId
        passport {
          uid
        }
        person {
          name {
            firstname
            middlename
            lastname
          }
          dob
        }
        insurance {
          insuranceType
          insuranceDetails
        }
      }
      created
      createdBy {
        person {
          firstname
          lastname
        }
        uuid
      }
      facility {
        name
        addresses {
          ward
          street
          subCounty
          state
        }
      }
      inpatientDetails {
        inpatientNumber
        admission
        discharge
        ward
        dailyRebateAmount
      }
      invoiceStatus
      balance {
        paid
        total
        due
        currency
      }
      lineItems {
        id
        priceListItem {
          name
          priceListItemType
        }
        quantity
        cost
        disabled
      }
      payers {
        type
        name
        balance {
          paid
          total
        }
        insuranceDetails {
          name
          insuranceNumber
          dependent
        }
      }
      transactions {
        payer {
          type
        }
        payment {
          paymentMethod
          authCode
        }
        refund {
          paymentMethod
          authCode
        }
        amount {
          value
        }
        currencyExchange {
          from {
            currency
            value
          }
          to {
            currency
            value
          }
          exchangeRate {
            id
            baseCurrency
            quoteCurrency
            rate
            effectiveDate
          }
        }
        actionedBy {
          person {
            firstname
            lastname
          }
        }
      }
    }
  }
`;

export default function useFetchPrintableInvoice(
  invoiceId: string,
  hookOptions?: QueryHookOptions<
    InvoiceForReceiptQuery,
    InvoiceForReceiptQueryVariables
  >,
) {
  return useInvoiceForReceiptQuery({
    variables: { invoiceId },
    ...hookOptions,
  });
}
